import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const DISMISSED_KEY = 'memberNest.pwaInstallDismissedAt';
const INSTALL_ACKNOWLEDGED_KEY = 'memberNest.pwaInstallAcknowledgedAt';
const DISMISS_DAYS = 21;

const PwaInstallContext = createContext(null);

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator?.standalone === true;
}

function isIosLike() {
  if (typeof window === 'undefined') return false;
  const platform = window.navigator?.platform || '';
  const userAgent = window.navigator?.userAgent || '';
  const touchMac = platform === 'MacIntel' && window.navigator?.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(userAgent) || touchMac;
}

function isAndroidLike() {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator?.userAgent || '';
  return /Android/i.test(userAgent);
}

function readDismissed() {
  try {
    const value = Number(window.localStorage.getItem(DISMISSED_KEY) || 0);
    if (!value) return false;
    return Date.now() - value < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (err) {
    return false;
  }
}

function readAcknowledged() {
  try {
    return Boolean(Number(window.localStorage.getItem(INSTALL_ACKNOWLEDGED_KEY) || 0));
  } catch (err) {
    return false;
  }
}

function writeAcknowledged() {
  try {
    window.localStorage.setItem(INSTALL_ACKNOWLEDGED_KEY, String(Date.now()));
  } catch (err) {}
}

async function detectRelatedPwaInstalled() {
  if (typeof navigator === 'undefined' || typeof navigator.getInstalledRelatedApps !== 'function') {
    return false;
  }
  try {
    const relatedApps = await navigator.getInstalledRelatedApps();
    return Array.isArray(relatedApps) && relatedApps.some((app) => (
      app?.platform === 'webapp'
      && (!app.url || new URL(app.url, window.location.origin).origin === window.location.origin)
    ));
  } catch (err) {
    return false;
  }
}

export function PwaInstallPromptProvider({ children }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installedThisBrowser, setInstalledThisBrowser] = useState(() => isStandaloneDisplay());
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplay());
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const [installAcknowledged, setInstallAcknowledged] = useState(() => readAcknowledged() || isStandaloneDisplay());
  const [relatedPwaInstalled, setRelatedPwaInstalled] = useState(false);

  useEffect(() => {
    const standaloneQuery = window.matchMedia?.('(display-mode: standalone)');
    const updateStandalone = () => {
      const standalone = isStandaloneDisplay();
      setIsStandalone(standalone);
      if (standalone) {
        setInstalledThisBrowser(true);
        setInstallAcknowledged(true);
        writeAcknowledged();
      }
    };
    const handlePrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const handleInstalled = () => {
      setInstalledThisBrowser(true);
      setInstallAcknowledged(true);
      setInstallPrompt(null);
      writeAcknowledged();
      updateStandalone();
    };
    standaloneQuery?.addEventListener?.('change', updateStandalone);
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);
    updateStandalone();
    return () => {
      standaloneQuery?.removeEventListener?.('change', updateStandalone);
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    detectRelatedPwaInstalled().then((detected) => {
      if (cancelled) return;
      if (detected) {
        setRelatedPwaInstalled(true);
        setInstallAcknowledged(true);
        writeAcknowledged();
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch (err) {}
  }, []);

  const acknowledgeInstall = useCallback(() => {
    setInstallAcknowledged(true);
    writeAcknowledged();
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return { outcome: 'unavailable' };
    const result = await installPrompt.prompt();
    setInstallPrompt(null);
    if (result?.outcome === 'accepted') {
      setInstalledThisBrowser(true);
      setInstallAcknowledged(true);
      writeAcknowledged();
    }
    return result;
  }, [installPrompt]);

  const value = useMemo(() => {
    const ios = isIosLike();
    const android = isAndroidLike();
    const mobileDevice = ios || android;
    const installed = Boolean(installedThisBrowser || relatedPwaInstalled || isStandalone);
    const openAppUiAvailable = Boolean(mobileDevice && !isStandalone && relatedPwaInstalled);
    const openInstalledAppMessage = ios
      ? 'Open Member Nest from your Home Screen for notifications and offline features.'
      : 'Open Member Nest from your app screen for notifications and offline features.';
    return {
      acknowledgeInstall,
      canPrompt: Boolean(mobileDevice && installPrompt && !installed),
      dismissed,
      dismiss,
      installAcknowledged,
      installed,
      installedThisBrowser,
      isAndroid: android,
      isIos: ios,
      isStandalone,
      openInstalledAppMessage,
      promptInstall,
      relatedPwaInstalled,
      shouldShowInstallUi: mobileDevice && !installed,
      shouldShowOpenAppUi: openAppUiAvailable,
    };
  }, [
    acknowledgeInstall,
    dismiss,
    dismissed,
    installAcknowledged,
    installPrompt,
    installedThisBrowser,
    isStandalone,
    promptInstall,
    relatedPwaInstalled,
  ]);

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePwaInstallPrompt() {
  const context = useContext(PwaInstallContext);
  if (!context) {
    throw new Error('usePwaInstallPrompt must be used inside PwaInstallPromptProvider');
  }
  return context;
}
